import { useCallback, useMemo, useState } from 'react';

export type SortDirection = 'asc' | 'desc';

export interface TableState {
  page: number;
  pageSize: number;
  sortBy: string | null;
  sortDir: SortDirection;
  search: string;
}

export const PAGE_SIZES = [10, 25, 50, 100] as const;

export interface TableStateApi {
  state: TableState;
  setState: (next: TableState) => void;
  setPage: (page: number) => void;
  setSearch: (search: string) => void;
  reset: () => void;
}

export interface TableStateOptions {
  pageSize?: number;
  sortBy?: string | null;
  sortDir?: SortDirection;
}

export function useTableState(options: TableStateOptions = {}): TableStateApi {
  const initial = useMemo<TableState>(
    () => ({
      page: 1,
      pageSize: options.pageSize ?? 25,
      sortBy: options.sortBy ?? null,
      sortDir: options.sortDir ?? 'asc',
      search: '',
    }),
    [options.pageSize, options.sortBy, options.sortDir],
  );

  const [state, setStateRaw] = useState<TableState>(initial);

  const setState = useCallback((next: TableState) => {
    setStateRaw(next);
  }, []);

  const setPage = useCallback((page: number) => {
    setStateRaw((current) => ({ ...current, page }));
  }, []);

  // Cambiar la búsqueda siempre vuelve a la primera página: quedarse en la 7 con
  // un resultado distinto produce una tabla vacía que parece un error.
  const setSearch = useCallback((search: string) => {
    setStateRaw((current) => ({ ...current, search, page: 1 }));
  }, []);

  const reset = useCallback(() => {
    setStateRaw(initial);
  }, [initial]);

  return { state, setState, setPage, setSearch, reset };
}
